import Layout from 'Nori/Layout';
import Button from 'Nori/Button';
import SplitContent from 'Custom/Split/SplitContent';
import Hero from 'Custom/Hero';
import RichText from 'Custom/RichText';
import AjustableImage from 'Custom/AjustableImage';
import styles from 'styles/pages/insuranceCoverage.module.scss';
import Container from 'Nori/Container';
import { Finder } from 'Custom/Finder';

import SEAWALL_IMAGE from 'Images/people/seawall.webp';
import SEAWALL_IMAGE_MOBILE from 'Images/people/seawall-mobile.webp';
import CIRCLE_IMAGE from 'Images/misc/x-yellow.webp';
import QUESTIONS_IMAGE from 'Images/misc/question-yellow.webp';
import CHECK_GOLD from 'Images/misc/check-gold.webp';

const HEAD = {
  title:
    'Insurance Coverage for NEXPLANON® (etonogestrel implant) 68 mg Radiopaque',
  description:
    'Learn about the insurance coverage for NEXPLANON® (etonogestrel implant) 68 mg Radiopaque. Check with your insurance plan for cost and coverage information.',
  keywords: 'insurance coverage nexplanon',
};

const HERO_IMAGE = {
  src: SEAWALL_IMAGE,
  fill: true,
  objectFit: 'cover',
  objectPosition: '70% top',
};

const HERO_MOBILE_IMAGE = {
  src: SEAWALL_IMAGE_MOBILE,
  fill: true,
  objectFit: 'cover',
  objectPosition: 'top center',
};

export default function InsuranceCoverage() {
  return (
    <Layout head={HEAD}>
      <Hero
        title="INSURANCE COVERAGE"
        caption="actor portrayal"
        image={HERO_IMAGE}
        mobileImage={HERO_MOBILE_IMAGE}
        imageBreakpoint="md"
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Hero"
        data-design-module_name="INSURANCE COVERAGE"
        data-design-module_no="01"
      >
        <RichText>
          <h2>how can you check if NEXPLANON is covered?</h2>
          <p>
            Once you and your doctor have decided that NEXPLANON is right for
            you, check with your insurance provider to determine your specific
            coverage.
          </p>
          <p>
            See how to determine if NEXPLANON is covered by your insurance plan
            below.
          </p>
        </RichText>
      </Hero>

      <section
        className={styles.copyBlock}
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="is NEXPLANON covered by your insurance?"
        data-design-module_no="02"
      >
        <Container size="md">
          <RichText className={styles.mdCopy}>
            <h2>is NEXPLANON covered by your insurance?</h2>
            <p className="intro">
              NEXPLANON may be covered as a medical benefit or as a pharmacy
              benefit. To find out if you have coverage for NEXPLANON as a
              medical benefit:
            </p>
          </RichText>
        </Container>
        <Container>
          <RichText className={styles.columns}>
            <div>
              <h3>call the customer service for your insurance</h3>
              <p>
                That number should be listed on the back of your health
                insurance card. Be sure to ask for the names of any people you
                speak with and write them down.
              </p>
            </div>
            <div>
              <h3>inform the representative about NEXPLANON</h3>
              <p>
                Tell them NEXPLANON is a physician-administered birth control
                drug (not a device), and that your physician will:
              </p>
              <ul className={`${styles.list} primary`}>
                <li>Prescribe NEXPLANON</li>
                <li>Order NEXPLANON for you</li>
                <li>Insert NEXPLANON during an office visit</li>
              </ul>
            </div>
            <div>
              <h3>ask if NEXPLANON is covered by your insurance</h3>
              <p>
                If the answer is no, then ask if you have coverage for NEXPLANON
                in your pharmacy benefit.
              </p>
              <p>
                To get information about pharmacy benefits, you may have to call
                another number. If so, repeat these steps with the pharmacy
                benefit representative.
              </p>
            </div>
          </RichText>
        </Container>
      </section>

      <SplitContent
        reverse
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="if NEXPLANON is covered"
        data-design-module_no="03"
      >
        <AjustableImage image={{ src: CHECK_GOLD }} />
        <>
          <RichText className={styles.copy}>
            <h2>if NEXPLANON is covered</h2>
            <p className="intro">
              You will want to verify some information with your insurance
              company to better understand your costs.
            </p>
            <h3>ask if NEXPLANON and the procedure are 100% covered</h3>
            <p className="intro">If not, ask about any out-of-pocket costs.</p>
            <p className="intro">
              You should also confirm about the coverage and out-of-pocket costs
              for removal.
            </p>
            <p className="intro">Keep in mind your insurance may change.</p>
            <h3>ask for verification of coverage</h3>
            <p className="intro">
              This step could be beneficial if for any reason a claim gets
              denied.
            </p>
            <h3>if written verification isn’t available</h3>
            <p className="intro">
              Make sure you get the name of the person you spoke with, and
              communicate with your healthcare professional what you learned
              about your coverage.
            </p>
            <h3>make an appointment</h3>
            <p className="intro">
              During this appointment, you will have your NEXPLANON birth
              control inserted in your arm.
            </p>
          </RichText>
        </>
      </SplitContent>

      <SplitContent
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="if NEXPLANON isn’t covered"
        data-design-module_no="04"
      >
        <AjustableImage
          background="secondary4Light"
          image={{ src: CIRCLE_IMAGE }}
        />
        <>
          <RichText className={styles.copy}>
            <h2>if NEXPLANON isn’t covered</h2>
            <p className="intro">You may have some other options.</p>
            <h3>ask if there are “Medical Exceptions” available</h3>
            <p className="intro">
              Some policies have medical exception clauses that allow your
              healthcare professional to authorize a specific treatment.
            </p>
            <h3>if a “Medical Exception” isn’t available</h3>
            <p className="intro">
              Ask your healthcare professional if they have any payment plans
              available to offset the cost.
            </p>
          </RichText>
        </>
      </SplitContent>

      <SplitContent
        reverse
        variant="questions"
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="still have questions?"
        data-design-module_no="05"
      >
        <AjustableImage
          background="secondary4Light"
          image={{ src: QUESTIONS_IMAGE }}
        />
        <>
          <RichText>
            <h2>still have questions?</h2>
            <p className="intro">Check out our FAQs.</p>
            <p className="intro">These may be helpful to you.</p>
          </RichText>
          <Button
            type="secondary4"
            href="/questions/"
            data-design-type="clickAction"
            data-design-category="button_vd"
            data-design-action="Button"
            data-design-label="Explore our FAQs"
            data-design-nori_id="Inline Content"
            data-design-module_name="still have questions?"
            data-design-module_no="05"
          >
            Explore our FAQs
          </Button>
        </>
      </SplitContent>

      <Finder data-design-module_no="06" />
    </Layout>
  );
}
